
import { mapState } from "pinia";
import { BNavItemDropdown } from "bootstrap-vue";
import { IDropdownItem, SearchMode, SearchType } from "@/types";
import GDropdown from "@/components/shared/GDropdown.vue";
import FlexibleText from "@/components/shared/FlexibleText.vue";
import CoordinateSearch from "@/components/search/CoordinateSearch.vue";
import DatasetSearch from "@/components/search/DatasetSearch.vue";
import AddressSearch from "@/components/search/AddressSearch.vue";
import { useAppConfig } from "@/config";
import { humanize, titleCase } from "@/util/helpers";
import {
  useAuth,
  useCognitoAdmin,
  useDataSource,
  useGammaData,
  useLayout,
  useMap,
  useOrgSettings,
} from "@/services";
import BusinessLineSelector from "@/components/BusinessLineSelector.vue";
import { defineComponent } from "vue";

export default defineComponent({
  name: "TheNavbar",
  components: {
    CoordinateSearch,
    GDropdown,
    FlexibleText,
    BusinessLineSelector,
  },
  data: () => ({
    navMenu: useAppConfig().menuTop,
    searchBy: {
      name: SearchType.Address,
      component: AddressSearch,
    } as SearchMode,
  }),
  computed: {
    username(): string {
      return this.user?.email;
    },
    userOrg(): string {
      return this.user?.["custom:organisation"];
    },
    ...mapState(useAuth, ["userInfo", "user", "isLoggedIn", "isInstanceReady"]),
    ...mapState(useLayout, ["page"]),
    ...mapState(useOrgSettings, ["settings", "searchProviderIndex"]),
    ...mapState(useMap, ["baseMap", "baseMaps"]),
    roles(): string[] {
      return useCognitoAdmin().currentUserGroups;
    },
    hasOrgSettings(): boolean {
      return !!useOrgSettings().settings;
    },
    isOnUserRoute(): boolean {
      return this.$route.name?.length
        ? !useAppConfig().adminRoutes.includes(this.$route.name)
        : true;
    },
    searchOptions(): IDropdownItem<SearchMode>[] {
      const searchOptions = (
        useOrgSettings().applicationSettings?.searchModes || [
          SearchType.Address,
        ]
      ).map((searchType) => this.toSearchModeOption(searchType, searchType));
      const datasetSearchOptions = useGammaData()
        .dataSources.map((name) => useDataSource(name)())
        .filter((dataSource) => !!dataSource.source.businessLine)
        .map((dataSource) =>
          this.toSearchModeOption(
            dataSource.name,
            SearchType.Dataset,
            dataSource.name
          )
        );
      return [...searchOptions, ...datasetSearchOptions];
    },
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          useCognitoAdmin().getCurrentUserGroups();
        }
      },
      immediate: true,
    },
    searchProviderIndex: {
      handler(val) {
        if (typeof val !== "number") {
          useOrgSettings().searchProviderIndex = 0;
        }
      },
      immediate: true,
    },
  },
  methods: {
    humanize,
    getComponent(type: SearchType): any {
      switch (type) {
        case SearchType.Coordinates:
          return CoordinateSearch;
        case SearchType.Dataset:
          return DatasetSearch;
        default:
          return AddressSearch;
      }
    },
    toSearchModeOption(
      name: string,
      type: SearchType,
      sourceName?: string
    ): IDropdownItem<SearchMode> {
      return {
        key: name,
        value: {
          name: titleCase(name) || "",
          sourceName,
          component: this.getComponent(type),
        },
      };
    },
    logout() {
      useCognitoAdmin()
        .logout()
        .then(() => this.$router.go(0));
    },
    goHome() {
      this.$router.push({ path: "/", query: {} }).then(() => {
        this.$router.go(0);
      });
    },
    showMenuItem(requiredRoles: string[]): boolean {
      return (
        requiredRoles.length === 0 ||
        this.roles?.some((r) => requiredRoles.includes(r))
      );
    },
    closeMenu(ref: string) {
      const menu = this.$refs[ref];
      if (menu instanceof BNavItemDropdown) {
        menu.hide(true);
      }
    },
    isCurrentPage(page: { path: string }): boolean {
      return (page.path.substring(1) || "map") === this.page;
    },
  },
});
